import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useGetApiChannelConnections,
} from "@/lib/api/generated/channels/channels";
import {
  useGetApiWorkflowPolicies,
  usePostApiWorkflowPolicies,
} from "@/lib/api/generated/workflows/workflows";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type {
  ChannelConnectionRecord,
  JsonObject,
  WorkflowPolicyRecord,
} from "../../../../../packages/shared-types/src/index.js";

const COMMON_ROLE_OPTIONS = ["member", "employee", "sales", "support"] as const;

const editWorkflowPolicySchema = z.object({
  enabled: z.boolean().default(true),
  allowedChannels: z.array(z.string()).default([]),
  allowedRoles: z.array(z.string()).default([]),
  config: z.string().default(""),
});

type EditWorkflowPolicyForm = z.infer<typeof editWorkflowPolicySchema>;

export function EditWorkflowPolicyPage() {
  const { workflowKey } = useParams<{ workflowKey: string }>();
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: policies, isLoading } = useGetApiWorkflowPolicies<WorkflowPolicyRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const { data: channelConnections } = useGetApiChannelConnections<ChannelConnectionRecord[]>({
    query: { enabled: Boolean(tenantId) },
  });
  const mutation = usePostApiWorkflowPolicies();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const policy = policies?.find((p) => p.workflowKey === workflowKey);
  const channelOptions = useMemo(
    () =>
      Array.from(
        new Set((channelConnections ?? []).map((entry) => entry.channelType).filter(Boolean)),
      ).toSorted(),
    [channelConnections],
  );
  const roleOptions = useMemo(
    () => Array.from(new Set([...(policy?.allowedRoles ?? []), ...COMMON_ROLE_OPTIONS])).toSorted(),
    [policy?.allowedRoles],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditWorkflowPolicyForm>({
    resolver: zodResolver(editWorkflowPolicySchema),
    defaultValues: {
      enabled: true,
      allowedChannels: [],
      allowedRoles: [],
      config: "",
    },
  });

  useEffect(() => {
    if (policy) {
      reset({
        enabled: policy.enabled,
        allowedChannels: policy.allowedChannels,
        allowedRoles: policy.allowedRoles,
        config: policy.config ? JSON.stringify(policy.config, null, 2) : "",
      });
      setShowAdvanced(Boolean(policy.config));
    }
  }, [policy, reset]);

  const allowedChannels = watch("allowedChannels");
  const allowedRoles = watch("allowedRoles");

  const onSubmit = async (data: EditWorkflowPolicyForm) => {
    try {
      let config: JsonObject | undefined;
      if (showAdvanced && data.config.trim()) {
        try {
          config = JSON.parse(data.config);
        } catch {
          toast.error("Invalid JSON in advanced config");
          return;
        }
      }

      await mutation.mutateAsync({
        data: {
          tenantId: tenantId!,
          workflowKey: workflowKey!,
          enabled: data.enabled,
          allowedChannels: data.allowedChannels,
          allowedRoles: data.allowedRoles,
          config,
        },
      });
      toast.success("Governance policy updated");
      navigate("/workspace/workflows");
    } catch {
      toast.error("Failed to update governance policy");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Loading...</p>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="max-w-2xl mx-auto">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
          Governance policy not found.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Edit Governance Policy
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Policy Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
              Workflow Key
            </span>
            <p className="text-xs font-mono mt-1" style={{ color: "var(--muted-foreground)" }}>
              {workflowKey}
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                <input type="checkbox" {...register("enabled")} />
                Enabled
              </label>
            </div>

            <SelectionField
              label="Allowed Channels"
              helper={
                allowedChannels.length === 0
                  ? "No channel restriction."
                  : `Restricted to: ${allowedChannels.join(", ")}`
              }
            >
              {channelOptions.length === 0 ? (
                <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  No channel connections configured for this tenant yet.
                </p>
              ) : (
                <ToggleGroup
                  options={channelOptions}
                  selected={allowedChannels}
                  onToggle={(value) =>
                    setValue("allowedChannels", toggleListValue(allowedChannels, value), {
                      shouldDirty: true,
                    })
                  }
                />
              )}
            </SelectionField>

            <SelectionField
              label="Allowed Roles"
              helper={
                allowedRoles.length === 0
                  ? "No role restriction."
                  : `Restricted to: ${allowedRoles.join(", ")}`
              }
            >
              <ToggleGroup
                options={roleOptions}
                selected={allowedRoles}
                onToggle={(value) =>
                  setValue("allowedRoles", toggleListValue(allowedRoles, value), {
                    shouldDirty: true,
                  })
                }
              />
            </SelectionField>

            <div className="rounded-[var(--radius-sm)] border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
                    Advanced JSON Overrides
                  </span>
                  <span className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                    Only use this when the workflow exposes extra policy config beyond the standard controls.
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvanced((current) => !current)}
                >
                  {showAdvanced ? "Hide Advanced" : "Show Advanced"}
                </Button>
              </div>
              {showAdvanced ? (
                <div className="mt-4">
                  <FormField label="Config (JSON)" error={errors.config?.message}>
                    <textarea
                      {...register("config")}
                      placeholder='{ "key": "value" }'
                      className="form-input font-mono"
                      rows={6}
                    />
                  </FormField>
                </div>
              ) : null}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Governance Policy"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace/workflows")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function toggleListValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

function SelectionField({
  label,
  helper,
  children,
}: {
  label: string;
  helper: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </span>
      {children}
      <span className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
        {helper}
      </span>
    </div>
  );
}

function ToggleGroup({
  options,
  selected,
  onToggle,
}: {
  options: readonly string[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const checked = selected.includes(option);
        return (
          <label
            key={option}
            className="flex items-center gap-2 rounded-[var(--radius-sm)] border px-3 py-2 cursor-pointer text-xs transition-colors"
            style={{
              background: checked ? "var(--accent-subtle)" : "transparent",
              borderColor: checked ? "var(--accent)" : "var(--border)",
              color: checked ? "var(--accent)" : "var(--text)",
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(option)}
              className="shrink-0"
            />
            {option}
          </label>
        );
      })}
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
